import { useMemo } from 'react'
import { AlertIcon, CheckIcon } from '../components/icons'
import { EuLabel } from '../components/EuLabel'
import { JobIcon } from '../components/JobIcon'
import { TyreFilterMenu } from '../components/TyreFilterMenu'
import { RUN_FLAT_WARNING } from '../components/TyreFitmentPicker'
import { formatMoney } from '../lib/format'
import {
  buildFacets,
  filterOffers,
  offerTotal,
  SEASON_LABELS,
  sortOffersByPrice,
  TIER_LABELS,
  type TyreOffer,
  type TyreOfferFilters,
} from '../lib/tyreOffers'
import {
  bestPromotion,
  describeSaving,
  promotionNudge,
  type TyrePromotion,
} from '../lib/tyrePromotions'

export interface TyreOffersStepProps {
  size: string
  quantity: number
  offers: TyreOffer[]
  loading: boolean
  error: string | null
  onRetry: () => void
  filters: TyreOfferFilters
  onFiltersChange: (filters: TyreOfferFilters) => void
  selectedOfferId: string | null
  onSelectOffer: (offer: TyreOffer) => void
  showPrices: boolean
  /** Quantity-based offers set by the garage. */
  promotions: TyrePromotion[]
  /** Raises the quantity, for the "add one more and save" nudge. */
  onQuantityChange: (quantity: number) => void
  /** The axle of a staggered car these tyres are for (ACA-98); absent for all four wheels. */
  axle?: 'front' | 'rear' | null
  /** The car's factory tyres are run-flats, so say so above the list. */
  runFlat?: boolean
}

/**
 * Choosing the actual tyre.
 *
 * Ordered cheapest-first because that is what customers scan for, with the
 * per-tyre price and the total for their quantity both shown — four tyres at
 * £142 is a £568 decision and hiding that until checkout loses trust.
 *
 * Filters are built from the results, so a facet never appears unless it would
 * actually narrow anything. Everything except brand, model and price is
 * optional: a supplier feed without EU label data still produces usable cards.
 */
export function TyreOffersStep({
  size,
  quantity,
  offers,
  loading,
  error,
  onRetry,
  filters,
  onFiltersChange,
  selectedOfferId,
  onSelectOffer,
  showPrices,
  promotions,
  onQuantityChange,
  axle = null,
  runFlat = false,
}: TyreOffersStepProps) {
  const facets = useMemo(() => buildFacets(offers), [offers])
  const visible = useMemo(
    () => sortOffersByPrice(filterOffers(offers, filters)),
    [offers, filters]
  )

  if (loading) {
    return (
      <div className="ac-stack" aria-busy="true">
        <span className="ac-sr-only">Finding tyres in {size}</span>
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className="ac-skeleton ac-skeleton--row" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="ac-stack">
        <div className="ac-notice ac-notice--error" role="alert">
          <AlertIcon size={18} />
          <span>{error}</span>
        </div>
        <button type="button" className="ac-btn ac-btn--secondary" onClick={onRetry}>
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="ac-stack">
      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">
          <JobIcon name="tyres" size={14} /> {size}
        </p>
        <h3 className="ac-heading">{axle ? `Choose your ${axle} tyres` : 'Choose your tyres'}</h3>
        <p className="ac-hint">Per tyre, fitted and including VAT.</p>
      </div>

      {runFlat ? (
        <div className="ac-notice ac-notice--info" role="note">
          <AlertIcon size={18} />
          <span>{RUN_FLAT_WARNING}</span>
        </div>
      ) : null}

      <TyreFilterMenu
        facets={facets}
        filters={filters}
        onChange={onFiltersChange}
        resultCount={visible.length}
      />

      {promotions.length > 0 ? (
        <div className="ac-deal" role="note">
          <span className="ac-deal__flash">Offer</span>
          <span>{promotions.map((promotion) => promotion.label).join(' · ')}</span>
        </div>
      ) : null}

      {visible.length === 0 ? (
        <div className="ac-empty">
          <p className="ac-prose">Nothing matches those filters.</p>
          <button
            type="button"
            className="ac-btn ac-btn--ghost"
            onClick={() => onFiltersChange({ brands: [], tiers: [], seasons: [] })}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <ul className="ac-list">
          {visible.map((offer) => {
            const selected = selectedOfferId === offer.id
            const total = offerTotal(offer, quantity)
            const promo = bestPromotion(promotions, offer, quantity)
            const nudge = promotionNudge(promotions, offer, quantity)

            return (
              <li key={offer.id}>
                <button
                  type="button"
                  className={`ac-offer${selected ? ' ac-offer--selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => onSelectOffer(offer)}
                >
                  <span className="ac-offer__check" aria-hidden="true">
                    <CheckIcon size={15} />
                  </span>

                  <span className="ac-offer__main">
                    <span className="ac-offer__head">
                      <span className="ac-offer__brand">{offer.brand}</span>
                      <span className="ac-offer__model">{offer.model}</span>
                    </span>

                    <span className="ac-offer__tags">
                      {offer.tier ? <span className="ac-tag ac-tag--accent">{TIER_LABELS[offer.tier]}</span> : null}
                      {offer.season && offer.season !== 'summer' ? (
                        <span className="ac-tag">{SEASON_LABELS[offer.season]}</span>
                      ) : null}
                      {offer.loadIndex || offer.speedRating ? (
                        <span className="ac-tag">
                          {offer.loadIndex}
                          {offer.speedRating}
                        </span>
                      ) : null}
                      {offer.runFlat ? <span className="ac-tag">Run-flat</span> : null}
                      {offer.extraLoad ? <span className="ac-tag">XL</span> : null}
                    </span>

                    <EuLabel offer={offer} />

                    {offer.availability ? (
                      <span className="ac-offer__stock">{offer.availability}</span>
                    ) : null}

                    {selected && nudge ? (
                      <span
                        className="ac-offer__nudge"
                        role="button"
                        tabIndex={0}
                        onClick={(event) => {
                          event.stopPropagation()
                          onQuantityChange(nudge.promotion.minQuantity)
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return
                          event.preventDefault()
                          event.stopPropagation()
                          onQuantityChange(nudge.promotion.minQuantity)
                        }}
                      >
                        Add {nudge.additionalQuantity} more and save{' '}
                        {formatMoney(nudge.savingAtThreshold)}. Tap to change to{' '}
                        {nudge.promotion.minQuantity}
                      </span>
                    ) : null}
                  </span>

                  {showPrices ? (
                    <span className="ac-offer__pricing">
                      <span className="ac-offer__price">{formatMoney(offer.fullyFittedPrice)}</span>
                      <span className="ac-offer__unit">per tyre</span>
                      {quantity > 1 ? (
                        promo ? (
                          <>
                            <span className="ac-offer__was">{formatMoney(total)}</span>
                            <span className="ac-offer__total">
                              {formatMoney(promo.discountedTotal)} for {quantity}
                            </span>
                            <span className="ac-offer__saving">{describeSaving(promo)}</span>
                          </>
                        ) : (
                          <span className="ac-offer__total">
                            {formatMoney(total)} for {quantity}
                          </span>
                        )
                      ) : null}
                    </span>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
